import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
// mui
import { Box, Card, Skeleton, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Iconify from '../../components/Iconify';
// Google Maps, ref: https://console.cloud.google.com/google/maps-apis/studio/styles
import { Loader } from '@googlemaps/js-api-loader';
import style from './style.json';
// graphql
import { AnalyticsGQLFunction } from '../../graphql/ANALYTICS';

export default function Map() {
  const { t } = useTranslation();
  const googleMapDiv = useRef(null);
  const mapInstance = useRef(null);
  const [isSynced, setIsSynced] = useState(false);
  const [locating, setLocating] = useState(false);

  // Configuration
  const MAXIMUM_QUERY_LIMIT = 5000;
  const LIMIT = 500;
  
  const now = useMemo(() => new Date(), []);
  
  const START = useMemo(() => (process.env.REACT_APP_NODE_ENV === 'development'
      ? JSON.stringify(1678406400000)
      : JSON.stringify(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 2, 0, 0, 0))), [now]);
  
  const END = useMemo(() => (process.env.REACT_APP_NODE_ENV === 'development'
      ? JSON.stringify(Date.now() + 1000 * 60 * 60 * 24 * 365) // 1 year from now
      : JSON.stringify(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59))), [now]);

  const { data, loading, fetchMore, refetch } = useQuery(AnalyticsGQLFunction.LAT_LONG_DATA, {
    variables: { start: START, end: END, lastId: null, limit: LIMIT },
    notifyOnNetworkStatusChange: true,
  });

  const [syncLocations, { loading: syncing }] = useMutation(AnalyticsGQLFunction.SYNC_UA_LOGS_LOCATION);

  const handleSync = async () => {
    console.log('[Map] Syncing range:', new Date(JSON.parse(START)).toLocaleString(), 'to', new Date(JSON.parse(END)).toLocaleString());
    try {
      await syncLocations({ variables: { start: START, end: END } });
      setIsSynced(true);
      refetch();
    } catch (error) {
      console.error('[Map] Sync failed:', error);
    }
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert(t('common.geo_not_supported'));
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (mapInstance.current) {
          const newCenter = { lat: latitude, lng: longitude };
          mapInstance.current.setCenter(newCenter);
          mapInstance.current.setZoom(14);
        }
        setLocating(false);
      },
      (error) => {
        console.error('[Map] Geolocation error:', error);
        alert(`Error: ${error.message}`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  };

  const loader = new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    version: 'weekly',
  });

  const createMapOptions = google => {
    const maxLat = Math.atan(Math.sinh(Math.PI)) * 180 / Math.PI;
    return {
      center: new google.maps.LatLng(23.6, 121),
      zoom: 7.8,
      disableDefaultUI: true,
      styles: style,
      restriction: {
        latLngBounds: { north: maxLat, south: -maxLat, west: -180, east: 180 },
        strictBounds: true,
      },
    };
  };

  useEffect(() => {
    if (!loading && data) {
      const logs = data.UALogsWithLatLongbyTime.UALogs;
      console.log(`[Map] Processing ${logs.length} logs...`);

      if (data.UALogsWithLatLongbyTime.MORE && logs.length < MAXIMUM_QUERY_LIMIT) {
        fetchMore({
          variables: {
            start: START,
            end: logs[logs.length - 1].t,
            lastId: logs[logs.length - 1].id,
            limit: LIMIT,
          },
        });
      } else {
        loader.load().then(google => {
          if (!mapInstance.current) {
            mapInstance.current = new google.maps.Map(googleMapDiv.current, createMapOptions(google));
            
            // Initial styling
            const updateStyle = () => {
              const zoom = mapInstance.current.getZoom();
              // Flattened scale: base 2.5, slightly shrinking as we zoom in
              const newScale = Math.max(0.8, 2.5 - (zoom - 7.8) * 0.15);
              
              mapInstance.current.data.setStyle({
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: '#FF0000',
                  fillOpacity: 1.0,
                  strokeWeight: 0,
                  scale: newScale,
                },
              });
            };

            // Listen for zoom changes to adjust dot size
            mapInstance.current.addListener('zoom_changed', updateStyle);
            updateStyle(); // Apply initially
          }

          const features = logs
            .filter(log => log.lat && log.lng)
            .map(log => ({
              type: 'Feature',
              id: log.id,
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(log.lng), parseFloat(log.lat)]
              },
              properties: {
                t: log.t
              }
            }));

          console.log(`[Map] Visualizing ${features.length} points.`);
          
          // Efficiently update data layer
          mapInstance.current.data.forEach(f => mapInstance.current.data.remove(f));
          mapInstance.current.data.addGeoJson({ type: 'FeatureCollection', features });

          // Auto-zoom to data if available and map isn't already interacted with
          if (features.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            features.forEach(f => {
              bounds.extend(new google.maps.LatLng(f.geometry.coordinates[1], f.geometry.coordinates[0]));
            });
            // Only auto-center if first load or after sync
            if (isSynced ) {
               mapInstance.current.fitBounds(bounds);
               if (mapInstance.current.getZoom() > 14) mapInstance.current.setZoom(14);
            }
          }
        });
      }
    }
  }, [data, loading, START, END]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack direction="row" spacing={1} sx={{ zIndex: '2', position: 'absolute', top: 16, right: 16 }}>
        <Tooltip title={locating ? t('common.locating') : t('common.my_location')}>
          <IconButton
            size="small"
            onClick={handleCurrentLocation}
            disabled={locating}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.8)', 
              color: locating ? 'primary.main' : 'text.primary',
              '&:hover': { bgcolor: 'white' },
              boxShadow: 2
            }}
          >
            <Iconify icon={locating ? 'eos-icons:loading' : 'ic:round-my-location'} />
          </IconButton>
        </Tooltip>
        
        <LoadingButton
          variant="contained"
          size="small"
          loading={syncing}
          startIcon={<Iconify icon="ic:round-sync" />}
          onClick={handleSync}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.8)', 
            color: 'text.primary', 
            '&:hover': { bgcolor: 'white' },
            boxShadow: 2
          }}
        >
          {isSynced ? t('common.synced') : t('common.sync_locations')}
        </LoadingButton>
      </Stack>
      
      <Card>
        {loading && !data ? (
          <Skeleton width={'100%'} height={'60vh'} />
        ) : (
          <Box ref={googleMapDiv} style={{ height: '60vh', width: '100%' }} />
        )}
      </Card>
      
      {data && data.UALogsWithLatLongbyTime.UALogs.length === 0 && !loading && (
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 16, color: 'grey.500', bgcolor: 'rgba(255,255,255,0.7)', px: 1 }}>
          {t('common.no_location_data')}
        </Typography>
      )}
    </Box>
  );
}
